import React, { useContext } from 'react'
import SelectInput from 'components/generic/Input/SelectInput'
import { useForm } from 'react-hook-form'
import STATES_QUERY from 'components/api/queries/SiteStatesForCountry.gql'
import CITIES_QUERY from 'components/api/queries/SiteCitiesForState.gql'
import NEIGHBORHOODS_QUERY from 'components/api/queries/SiteNeighborhoodsForCity.gql'
import { useLazyQuery } from '@apollo/client'
import PrimaryButton from 'components/generic/PrimaryButton'
import { trackClickEvent } from 'utils/Analytics'
import {
  getCityFromIpIfValidCountry,
  getHeadingForUserType,
  getLocationDropDownData,
} from 'utils/getStartedUtils'
import { StepHeading, Form } from 'components/GetStarted/styles'
import { AppContext } from 'contexts/AppContext'
import { DEFAULT_COUNTRY } from 'components/GetStarted/constants'

const StepUserLocation = ({ data, userInput, onStepComplete }) => {
  const { domain } = useContext(AppContext)
  const cityFromIp = getCityFromIpIfValidCountry(data)
  const { register, handleSubmit, setValue, watch, formState } = useForm({
    mode: 'onChange',
    defaultValues: {
      country: userInput?.country || cityFromIp?.state?.country?.id,
      state: userInput?.state || cityFromIp?.state?.id,
      city: userInput?.city || cityFromIp?.id,
      neighborhood: userInput?.neighborhood,
    },
  })
  const { country: inputCountry, state, city } = watch()
  const { isValid, dirtyFields } = formState
  const [getStates, { loading: statesLoading, data: statesData }] = useLazyQuery(STATES_QUERY)
  const [getCities, { loading: citiesLoading, data: citiesData }] = useLazyQuery(CITIES_QUERY)
  const [
    getNeighborhoods,
    { loading: neighborhoodsLoading, data: neighborhoodsData },
  ] = useLazyQuery(NEIGHBORHOODS_QUERY)
  const countries = data?.site?.supported_countries ?? [DEFAULT_COUNTRY]
  const hasSingleCountrySupport = countries.length === 1
  const country = hasSingleCountrySupport ? countries[0].id : inputCountry

  const states = getLocationDropDownData({
    dependentField: country,
    dataFromSsr: data?.site?.get_started?.location_states,
    dataFromUserInput: userInput?.location?.states,
    dataFromQuery: statesData?.site?.get_started?.location_states,
    isLoading: statesLoading,
    hasDataChanged: dirtyFields?.country,
  })
  const cities = getLocationDropDownData({
    dependentField: state,
    dataFromSsr: data?.site?.get_started?.location_cities,
    dataFromUserInput: userInput?.location?.cities,
    dataFromQuery: citiesData?.site?.get_started?.location_cities,
    isLoading: citiesLoading,
    hasDataChanged: dirtyFields?.state,
  })
  const neighborhoods = getLocationDropDownData({
    dependentField: city,
    dataFromSsr: data?.site?.get_started?.location_neighborhoods,
    dataFromUserInput: userInput?.location?.neighborhoods,
    dataFromQuery: neighborhoodsData?.site?.get_started?.location_neighborhoods,
    isLoading: neighborhoodsLoading,
    hasDataChanged: dirtyFields?.city,
  })
  const showCountries = countries.length > 1
  const showNeighborhoods = !!city && neighborhoods.length > 0
  const heading = getHeadingForUserType({
    userType: userInput?.user_type,
    providerHeading: `Where are you applying for ${data?.site?.suffixed_job_name}?`,
    seekerHeading: `Where do you need a ${data?.site?.provider_name}?`,
    defaultHeading: 'What city are you searching in?',
  })

  const defaultSetValueOptions = { shouldValidate: true, shouldDirty: true }
  const onCountryChange = (evt) => {
    setValue('state', null, defaultSetValueOptions)
    setValue('city', null, defaultSetValueOptions)
    setValue('neighborhood', null, defaultSetValueOptions)

    const countryId = evt.target.value
    if (countryId) {
      getStates({ variables: { countryId } })
    }
  }
  const onStateChange = (evt) => {
    setValue('city', null, defaultSetValueOptions)
    setValue('neighborhood', null, defaultSetValueOptions)

    const stateId = evt.target.value
    if (stateId) {
      getCities({ variables: { stateId } })
    }
  }
  const onCityChange = (evt) => {
    setValue('neighborhood', null, defaultSetValueOptions)

    const cityId = evt.target.value
    if (cityId) {
      getNeighborhoods({ variables: { cityId } })
    }
  }
  const onFormSubmit = (formData) => {
    const state = states.find((state) => state.id === formData.state)
    const city = cities.find((city) => city.id === formData.city)
    onStepComplete({
      ...formData,
      location: {
        state,
        city,
        states,
        cities,
        neighborhoods,
      },
    })
  }

  return (
    <Form onSubmit={handleSubmit(onFormSubmit)}>
      <StepHeading>{heading}</StepHeading>
      {showCountries && (
        <SelectInput
          autoFocus={true}
          name='country'
          onChange={onCountryChange}
          label='Country'
          options={countries}
          ref={register({ required: 'required' })}
        />
      )}

      <SelectInput
        name='state'
        disabled={statesLoading}
        onChange={onStateChange}
        label='State/Province'
        options={states}
        isLoading={statesLoading}
        ref={register({ required: 'required' })}
      />

      <SelectInput
        name='city'
        disabled={citiesLoading}
        onChange={onCityChange}
        label='City'
        options={cities}
        isLoading={citiesLoading}
        ref={register({ required: 'required' })}
      />

      {showNeighborhoods && (
        <SelectInput
          name='neighborhood'
          disabled={neighborhoodsLoading}
          label='Neighborhood'
          options={neighborhoods}
          isLoading={neighborhoodsLoading}
          ref={register()}
        />
      )}

      <PrimaryButton
        mt='2rem'
        disabled={!isValid}
        onClick={() => trackClickEvent(domain, 'get-started-next-user-location')}
      >
        Next
      </PrimaryButton>
    </Form>
  )
}

export default StepUserLocation
